import { useState } from 'react'
import {
  Download,
  Eye,
  EyeOff,
  GripVertical,
  Layers,
  Loader2,
  Play,
  Scissors,
  SlidersHorizontal,
  Snowflake,
  Square,
  Trash2,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { fmt, formatSpeed, pluralize } from '../format'
import { clipColor, clipDuration, clipLabel, type ToolProject } from '../model'
import { InlineNameEdit } from './InlineNameEdit'
import shell from '../styles/shell.module.scss'
import styles from '../styles/clips.module.scss'

/** Everything the footer needs to render one running/finished export.
 * Both exports share it because only one can run at a time. */
export interface ExportState {
  running: boolean
  progress: number
  message: string
  error: string | null
  /** Result of the last successful "single video" export, if still valid. */
  merged: { url: string; filename: string; duration: number } | null
}

export interface ClipsPanelProps {
  project: ToolProject
  /** 1-based position of each video, for the source badge on a clip. */
  videoNumberById: Record<string, number>
  /** Clip currently bounded by a preview, if any. */
  previewClipId: number | null
  /** Clip whose inspector is open, if any. */
  selectedClipId: number | null
  /** Open (or close) the clip inspector — speed, held frames, visibility. */
  onSelectClip: (clipId: number | null) => void
  /** Keep a clip in the list but out of the cut. */
  onToggleHidden: (clipId: number) => void
  /** True while the timeline is showing only the clips. */
  sequenceMode: boolean
  /** Whether the player is actually running. The button has to follow
   * *playback*, not the mode: keyed off the mode alone it stayed on "stop"
   * after stopping — still in "só os cortes", so no way to start again. */
  isPlaying: boolean
  onPlayClip: (index: number) => void
  onPlaySequence: () => void
  onStopPreview: () => void
  onRenameClip: (clipId: number, name: string) => void
  onDeleteClip: (clipId: number) => void
  /** ``insertAt`` is an insertion point in the current list. */
  onReorder: (from: number, insertAt: number) => void
  exportState: ExportState
  onExportClips: () => void
  onExportMerged: () => void
}

export function ClipsPanel({
  project,
  videoNumberById,
  previewClipId,
  selectedClipId,
  onSelectClip,
  onToggleHidden,
  sequenceMode,
  isPlaying,
  onPlayClip,
  onPlaySequence,
  onStopPreview,
  onRenameClip,
  onDeleteClip,
  onReorder,
  exportState,
  onExportClips,
  onExportMerged,
}: ClipsPanelProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dropAt, setDropAt] = useState<number | null>(null)
  // Rows are only draggable while the grip is held. Without this, dragging
  // to select text in an open rename field would drag the row instead.
  const [gripArmedId, setGripArmedId] = useState<number | null>(null)

  const clips = project.clips
  const multiVideo = project.videos.length > 1
  const sequencePlaying = sequenceMode && isPlaying
  // Hidden clips are in neither the preview nor the export, so a list of
  // only-hidden clips has nothing to export even though it isn't empty.
  const visibleCount = clips.filter((c) => !c.hidden).length
  const canExport = visibleCount > 0 && !exportState.running

  const previewingClipId = previewClipId

  const endDrag = () => {
    setDragIndex(null)
    setDropAt(null)
    setGripArmedId(null)
  }

  return (
    <aside className={styles.clipsPanel}>
      <div className={styles.cpHead}>
        <h2>Clipes</h2>
        <div className={styles.cpHeadRight}>
          <span className={styles.cpCount}>{pluralize(clips.length, 'clipe', 'clipes')}</span>
          <button
            type="button"
            className={cn(styles.cpPlayAll, sequencePlaying && styles.cpPlayAllActive)}
            onClick={sequencePlaying ? onStopPreview : onPlaySequence}
            disabled={clips.length === 0}
            title={
              sequencePlaying
                ? 'Parar a prévia'
                : 'Reproduzir os clipes na ordem, como ficará o vídeo único'
            }
            aria-label={sequencePlaying ? 'Parar prévia da sequência' : 'Reproduzir todos os clipes na ordem'}
          >
            {sequencePlaying ? <Square aria-hidden /> : <Play aria-hidden />}
          </button>
        </div>
      </div>

      <div className={styles.cpList}>
        {clips.length === 0 ? (
          <div className={styles.cpEmpty}>
            <Scissors aria-hidden />
            <p>Nenhum clipe ainda. Marque trechos do vídeo para criar cortes.</p>
          </div>
        ) : (
          clips.map((clip, index) => {
            const color = clipColor(clip.id)
            const label = clipLabel(clip, index)
            const isPreviewing = previewingClipId === clip.id
            return (
              <div
                key={clip.id}
                className={cn(
                  styles.clipItem,
                  isPreviewing && styles.clipItemPlaying,
                  clip.hidden && styles.clipItemHidden,
                  selectedClipId === clip.id && styles.clipItemSelected,
                  dragIndex === index && styles.clipItemDragging,
                  dropAt === index && styles.clipItemDropBefore,
                  dropAt === index + 1 && styles.clipItemDropAfter,
                )}
                style={{ ['--ci-c' as string]: color.border }}
                draggable={gripArmedId === clip.id}
                onDragStart={(e) => {
                  setDragIndex(index)
                  e.dataTransfer.effectAllowed = 'move'
                  // Firefox refuses to start a drag without payload.
                  e.dataTransfer.setData('text/plain', String(index))
                }}
                onDragOver={(e) => {
                  if (dragIndex === null) return
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                  const rect = e.currentTarget.getBoundingClientRect()
                  const after = e.clientY - rect.top > rect.height / 2
                  setDropAt(after ? index + 1 : index)
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  if (dragIndex !== null && dropAt !== null) onReorder(dragIndex, dropAt)
                  endDrag()
                }}
                onDragEnd={endDrag}
              >
                {/* Sits behind the controls and covers the row: clicking
                    anywhere but a control previews the clip. */}
                <button
                  type="button"
                  className={styles.ciRowButton}
                  onClick={() => {
                    onPlayClip(index)
                  }}
                  onKeyDown={(e) => {
                    // Keyboard equivalent of the drag handle.
                    if (!e.altKey) return
                    if (e.key === 'ArrowUp') {
                      e.preventDefault()
                      onReorder(index, index - 1)
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault()
                      onReorder(index, index + 2)
                    }
                  }}
                  aria-label={`Reproduzir ${label}`}
                  title="Clique para reproduzir o clipe · Alt+↑/↓ reordena"
                />
                <span
                  className={styles.ciGrip}
                  aria-hidden
                  title="Arraste para reordenar"
                  onMouseDown={() => {
                    setGripArmedId(clip.id)
                  }}
                  onMouseUp={() => {
                    setGripArmedId(null)
                  }}
                >
                  <GripVertical />
                </span>
                <div className={styles.ciBar} />
                <div className={styles.ciBody}>
                  <div className={styles.ciTitleRow}>
                    <span className={styles.ciPos}>{index + 1}</span>
                    <div className={styles.ciTitle}>
                      <InlineNameEdit
                        value={label}
                        ariaLabel={`Renomear ${label}`}
                        onCommit={(next) => {
                          onRenameClip(clip.id, next)
                        }}
                      />
                    </div>
                  </div>
                  <div className={styles.ciTime}>
                    {multiVideo ? (
                      <span className={styles.ciSource}>V{videoNumberById[clip.videoId] ?? '?'}</span>
                    ) : null}
                    {fmt(clip.start)} → {fmt(clip.end)}
                    {clip.speed !== 1 ? (
                      <span className={styles.ciFlag}>{formatSpeed(clip.speed)}</span>
                    ) : null}
                    {clip.freezes.length > 0 ? (
                      <span className={styles.ciFlag}>
                        <Snowflake aria-hidden />
                        {clip.freezes.length}
                      </span>
                    ) : null}
                  </div>
                </div>
                {/* The length **on the timeline**, which stopped matching
                    the footage the moment speed and freezes existed. */}
                <div className={styles.ciDur}>{fmt(clipDuration(clip))}</div>
                <button
                  type="button"
                  className={styles.ciIconBtn}
                  onClick={() => {
                    onToggleHidden(clip.id)
                  }}
                  aria-pressed={clip.hidden}
                  aria-label={clip.hidden ? `Incluir ${label} no corte` : `Ocultar ${label} do corte`}
                  title={
                    clip.hidden
                      ? 'Oculto: fora da prévia e da exportação'
                      : 'Ocultar do corte (continua na lista)'
                  }
                >
                  {clip.hidden ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
                </button>
                <button
                  type="button"
                  className={styles.ciIconBtn}
                  onClick={() => {
                    onSelectClip(selectedClipId === clip.id ? null : clip.id)
                  }}
                  aria-pressed={selectedClipId === clip.id}
                  aria-label={`Ajustes de ${label}`}
                  title="Velocidade e frames congelados"
                >
                  <SlidersHorizontal aria-hidden />
                </button>
                {clip.download ? (
                  // target=_blank keeps the editor tab alive even when the
                  // browser opens the MP4 inline instead of downloading
                  // (cross-origin in prod); rel=noopener is the usual
                  // anti-tabnab hardening.
                  <a
                    href={clip.download.url}
                    download={clip.download.filename}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.ciDownload}
                    aria-label={`Baixar ${label}`}
                    title="Baixar MP4"
                  >
                    <Download aria-hidden />
                  </a>
                ) : null}
                <button
                  type="button"
                  className={styles.ciDel}
                  onClick={() => {
                    onDeleteClip(clip.id)
                  }}
                  aria-label={`Excluir ${label}`}
                >
                  <Trash2 aria-hidden />
                </button>
              </div>
            )
          })
        )}
      </div>

      <div className={styles.cpFoot}>
        {exportState.running ? (
          <div className={shell.progressCard}>
            <div className={shell.progressHeader}>
              <Loader2 className={cn(shell.progressIcon, shell.spin)} aria-hidden />
              <span className={shell.progressMsg}>{exportState.message || 'Cortando...'}</span>
              <span className={shell.progressPct}>{exportState.progress}%</span>
            </div>
            <div className={shell.progressBar}>
              <div
                className={shell.progressFill}
                style={{ width: `${String(exportState.progress)}%` }}
              />
            </div>
          </div>
        ) : (
          <>
            <button
              type="button"
              className={styles.expBtn}
              onClick={onExportClips}
              disabled={!canExport}
              title={
                canExport
                  ? 'Corta cada clipe em um MP4 separado'
                  : 'Marque ao menos um clipe para exportar'
              }
            >
              <Download aria-hidden />
              <span>Exportar clipes</span>
            </button>
            <button
              type="button"
              className={styles.expBtn}
              onClick={onExportMerged}
              disabled={!canExport}
              title={
                canExport
                  ? 'Junta todos os clipes, na ordem da lista, em um único MP4'
                  : 'Marque ao menos um clipe para exportar'
              }
            >
              <Layers aria-hidden />
              <span>Gerar vídeo único</span>
            </button>
            {exportState.merged ? (
              <a
                href={exportState.merged.url}
                download={exportState.merged.filename}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.mergedDownload}
              >
                <Download aria-hidden />
                <span>Baixar vídeo único · {fmt(exportState.merged.duration)}</span>
              </a>
            ) : null}
            {exportState.error ? <p className={shell.error}>{exportState.error}</p> : null}
          </>
        )}
      </div>
    </aside>
  )
}
